import { atom } from 'jotai'
import { APIDiscount, APIPrice, ProcessedPhase } from './scheduleData.atom'
import { processedPhasesAtom } from './processedPhases.atom'
import {
  getBillingDates,
  calculateDiscountedPrice,
  toBillingFrequencyLabel,
  toMoney,
  toPriceSummary
} from '@sequencehq/utils'
import { add, isAfter, isEqual, sub } from '@sequencehq/utils/dates'
import omit from 'lodash/fp/omit'
import { dequal } from 'dequal'

export type PriceDetails = {
  id: string
  summary: {
    price: string
    discountedPrice: string
    frequency: string
  }
  inAdvance: boolean
  isOneTime: boolean
  name: string
}

const getPeriodDatesForPrice = ({
  billingDatesForPrice,
  startDate,
  endDate
}: {
  billingDatesForPrice: Array<Date>
  startDate: Date
  endDate?: Date
}): Array<{ start: Date; end: Date }> => {
  return billingDatesForPrice
    .filter(date => isAfter(date, startDate))
    .map((billingDate, idx) => {
      const periodStartDate = idx ? billingDatesForPrice[idx - 1] : startDate
      return {
        start: periodStartDate,
        end:
          endDate && isAfter(billingDate, endDate)
            ? endDate
            : sub(billingDate, { days: 1 })
      }
    })
}

type PriceDiscounts = {
  own?: APIDiscount
  global?: APIDiscount
}

const getDiscountForPrice =
  (ctx: { phaseDiscounts: Array<APIDiscount> }) =>
  (priceId: string): PriceDiscounts => {
    return {
      own: ctx.phaseDiscounts.find(discount =>
        discount.restrictToPrices.includes(priceId)
      ),
      global: ctx.phaseDiscounts.find(
        discount => !discount.restrictToPrices.length
      )
    }
  }

const checkHasPricingChanged = ({
  prevPrice,
  prevDiscounts,
  currentPrice,
  currentDiscounts
}: {
  prevPrice?: APIPrice
  prevDiscounts?: PriceDiscounts
  currentPrice: APIPrice
  currentDiscounts: PriceDiscounts
}) => {
  if (!prevPrice) {
    return true
  }

  const discountsChanged = !dequal(
    {
      own: currentDiscounts.own
        ? omit('restrictToPrices')(currentDiscounts.own)
        : undefined,
      global: currentDiscounts.global
        ? omit('restrictToPrices')(currentDiscounts.global)
        : undefined
    },
    {
      own: prevDiscounts?.own
        ? omit('restrictToPrices')(prevDiscounts.own)
        : undefined,
      global: prevDiscounts?.global
        ? omit('restrictToPrices')(prevDiscounts.global)
        : undefined
    }
  )
  const priceChanged = !dequal(omit('id')(prevPrice), omit('id')(currentPrice))

  return discountsChanged || priceChanged
}

const getPricingSummary = ({
  price,
  discounts
}: {
  price: APIPrice
  discounts: PriceDiscounts
}) => {
  /**
   * Only the price's own discount changes the rate we show. A phase-wide discount
   * covers the whole phase, so it is left out of the per-price summary.
   */
  const discountedPrice = calculateDiscountedPrice({
    price,
    discount: discounts.own && {
      ...discounts.own,
      discountCalculationType: discounts.own.type
    }
  })
  const fullPriceSummary = toPriceSummary(price)
  const discountedPriceSummary =
    typeof discountedPrice === 'number'
      ? toMoney({
          value: discountedPrice.toString(),
          currency: price.currency
        })
      : null

  return {
    price: fullPriceSummary,
    discountedPrice:
      discountedPriceSummary &&
      toMoney({
        value: discountedPriceSummary.toString(),
        currency: price.currency
      }) !== fullPriceSummary
        ? discountedPriceSummary
        : '',
    frequency: toBillingFrequencyLabel(price.billingFrequency)
  }
}

type ProductPriceHistory = {
  billingPeriods: Array<{ start: Date; end: Date }>
  openEndedStartDate?: Date
  prices: Array<{
    id: string
    summary: {
      price: string
      discountedPrice: string
      frequency: string
    }
    inAdvance: boolean
    isOneTime: boolean
    name: string
    startDate: Date
  }>
}

export const getExistingProductInformation =
  (ctx: { allPhases: Array<ProcessedPhase>; currentPhaseId: string }) =>
  (productId: string) => {
    const currentPhaseIndex = ctx.allPhases.findIndex(
      p => p.id === ctx.currentPhaseId
    )
    const previousPhase = ctx.allPhases[currentPhaseIndex - 1]

    const previousPrice = previousPhase?.prices.find(
      p => p.productId === productId
    )
    const previousDiscounts =
      previousPrice &&
      getDiscountForPrice({
        phaseDiscounts: previousPhase?.discounts ?? []
      })(previousPrice?.id)

    return {
      previousPrice,
      previousDiscounts
    }
  }

const applyProductTimelineInformationFromPhase =
  (ctx: {
    currentPriceHistory: Record<string, ProductPriceHistory>
    allPhases: Array<ProcessedPhase>
  }) =>
  (phase: ProcessedPhase) => {
    const isPhaseBillingReset = phase.recurrencePreference === 'RESET'
    const phaseIdx = ctx.allPhases.findIndex(p => p.id === phase.id)
    const isLastPhase = phaseIdx === ctx.allPhases.length - 1
    const previousPhase = ctx.allPhases[phaseIdx - 1]
    const phaseProducts = phase.prices.map(price => price.productId) ?? []

    const productsRemovedInThisPhase = previousPhase
      ? previousPhase.prices
          .map(price => price.productId)
          .filter(pid => !phaseProducts.includes(pid))
      : []

    /**
     * When a price is no longer present in the phase, but was in the last
     * phase, cap the end date of the price
     * to the day before the start of this phase.
     */
    const productHistoryWithUpdatesForNoPriceApplied =
      productsRemovedInThisPhase.reduce((acc, productId) => {
        if (!acc[productId]) {
          return acc
        }

        const latestBillingPeriod = acc[productId].billingPeriods.at(-1)

        if (!latestBillingPeriod) {
          return acc
        }

        return {
          ...acc,
          [productId]: {
            ...acc[productId],
            billingPeriods: [
              ...acc[productId].billingPeriods.slice(0, -1),
              {
                ...latestBillingPeriod,
                end: sub(phase.startDate, { days: 1 })
              }
            ]
          }
        }
      }, ctx.currentPriceHistory)

    /**
     * Otherwise, calculate and add the new billing periods for the product
     * in the context of this phase.
     */
    return phase.prices.reduce((acc, price) => {
      const { previousDiscounts, previousPrice } =
        getExistingProductInformation({
          allPhases: ctx.allPhases,
          currentPhaseId: phase.id
        })(price.productId)

      const discountsAppliedToThisPrice = getDiscountForPrice({
        phaseDiscounts: phase.discounts
      })(price.id)

      const hasPricingChanged = checkHasPricingChanged({
        prevPrice: previousPrice,
        prevDiscounts: previousDiscounts,
        currentPrice: price,
        currentDiscounts: discountsAppliedToThisPrice
      })

      /**
       * We generate billing periods so that the laast billing period of the
       * previous phase is accounted for as well, and then we will strip
       * the last billing period from the previous phase when we combine them.
       *
       * This ensures we correctly stitch together the billing periods across
       * phases, even when the phases are not aligned with the billing
       * periods.
       *
       * However, if we reset the billing period instead, then we want to
       * instead modify the last billing period of the previous phase to end
       * the day before the start of this phase.
       *
       * It's all quite fun, isn't it?
       */
      const previousBillingPeriods = acc[price.productId]?.billingPeriods ?? []
      const startOfLatestBillingPeriod =
        previousBillingPeriods[previousBillingPeriods.length - 1]?.start
      const endOfLatestBillingPeriod =
        previousBillingPeriods[previousBillingPeriods.length - 1]?.end
      const isAContiguousPhaseWithNoProration = isEqual(
        add(endOfLatestBillingPeriod, { days: 1 }),
        phase.startDate
      )

      const startDateForBillingPeriod =
        isPhaseBillingReset ||
        !startOfLatestBillingPeriod ||
        !previousPrice ||
        isAContiguousPhaseWithNoProration
          ? phase.startDate
          : startOfLatestBillingPeriod

      const billingDatesForPhaseForPrice = getBillingDates({
        recurrenceDayOfMonth: phase.recurrenceDayOfMonthForPhase,
        phaseDates: {
          start: hasPricingChanged
            ? phase.startDate
            : startDateForBillingPeriod,
          end: phase.endDate
        }
        /**
         * For one time prices, we want to 'fake' a monthly billing period, and
         * then choose the first 'monthly' billing period for our representation.
         * It's all just visual gubbins, since having no footprint at all on the
         * timeline is not great!
         */
      })[
        price.billingFrequency === 'ONE_TIME'
          ? 'MONTHLY'
          : price.billingFrequency
      ]

      const allNewBillingPeriods = getPeriodDatesForPrice({
        billingDatesForPrice: billingDatesForPhaseForPrice,
        startDate: startDateForBillingPeriod,
        endDate: isLastPhase
          ? phase.endDate
          : billingDatesForPhaseForPrice[
              billingDatesForPhaseForPrice.length - 1
            ]
      })
      const newBillingPeriods =
        price.billingFrequency === 'ONE_TIME'
          ? allNewBillingPeriods.slice(0, 1)
          : allNewBillingPeriods

      const latestBillingPeriod = previousBillingPeriods.at(-1)
      const latestBillingPeriodOverlapsWithNewBillingPeriods =
        latestBillingPeriod
          ? isAfter(latestBillingPeriod.end, newBillingPeriods[0].start)
          : false

      /**
       * When the latest billing period overlaps with the new billing period,
       * we want to trim that previous billing period to end the day before the start
       * of the new billing period.
       *
       * We don't want to always do 'capping' because if the previous billing period
       * ended well before this new one (i.e. a product is reintroduced after a gap)
       * then we would instead accidentally extend the previous billing period, which
       * ended correctly.
       */
      const updatedBillingPeriods =
        latestBillingPeriodOverlapsWithNewBillingPeriods
          ? [
              ...previousBillingPeriods.slice(0, -1),
              ...(isPhaseBillingReset && latestBillingPeriod
                ? [
                    {
                      ...latestBillingPeriod,
                      end: sub(phase.startDate, { days: 1 })
                    }
                  ]
                : []),
              ...newBillingPeriods
            ]
          : [...previousBillingPeriods, ...newBillingPeriods]

      if (!hasPricingChanged) {
        return {
          ...acc,
          [price.productId]: {
            ...acc[price.productId],
            openEndedStartDate: phase.openEnded ? phase.startDate : undefined,
            billingPeriods: updatedBillingPeriods
          }
        }
      }

      return {
        ...acc,
        [price.productId]: {
          openEndedStartDate: phase.openEnded ? phase.startDate : undefined,
          prices: [
            ...(acc[price.productId]?.prices ?? []),
            {
              id: price.id,
              summary: getPricingSummary({
                price,
                discounts: discountsAppliedToThisPrice
              }),
              inAdvance: price.billingType === 'IN_ADVANCE',
              isOneTime: price.billingFrequency === 'ONE_TIME',
              name: price.name,
              startDate: phase.startDate
            }
          ],
          billingPeriods: updatedBillingPeriods
        }
      }
    }, productHistoryWithUpdatesForNoPriceApplied)
  }

export const getProductPriceHistory = ({
  phases
}: {
  phases: Array<ProcessedPhase>
}): Record<string, ProductPriceHistory> => {
  if (!phases.length) {
    return {}
  }

  const productTimelines = phases.reduce(
    (acc: Record<string, ProductPriceHistory>, phase) => {
      return applyProductTimelineInformationFromPhase({
        currentPriceHistory: acc,
        allPhases: phases
      })(phase)
    },
    {}
  )

  return productTimelines
}
/**
 * This selector is the core of the timeline view's data. It's how
 * we take the API data and transform it into something we can use.
 * We generate the expected billing dates and create the product-first
 * timeline of billing periods along with transitions between prices
 * and different states (e.g. ongoing vs. finite billing).
 *
 * It's a complicated beastie, and one day it'd be nice to be on the
 * service side driving billing previews. But for now, this is what we
 * have!
 * @param param0
 * @returns
 */

export const productPriceHistoryAtom = atom<
  Record<string, ProductPriceHistory>
>(get => {
  const sortedPhases = get(processedPhasesAtom)

  return getProductPriceHistory({
    phases: sortedPhases
  })
})
